import { FC, useState } from 'react';
import { message, Tabs } from 'antd';
import {
    ApiOutlined,
    BankOutlined,
    CreditCardOutlined,
    GoldOutlined,
    SafetyOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { Card } from '../../../../../components/card/card';
import { Button, buttonType } from '../../../../../components/button/button';
import { API } from '../../../../../apis';
import { postViaAuth, responseValidator } from '../../../../../apis/api';
import {
    GatewayName,
    GatewaySettingDto,
    GatewaySettingItem,
    JibitPaymentSettings,
    PakatSettings,
    PayStarSettings,
    VandarSettings,
    ZibalSettings,
} from '../../../../../core/domain';
import { SettingsSection, SwitchField, TextInput } from '../shared/settings-ui';
import { useLoadSettings } from '../shared/use-settings';
import { useGoogleAuth } from '../shared/use-google-auth';
import '../settings.scss';

const GATEWAYS: { serviceName: GatewayName; title: string }[] = [
    { serviceName: GatewayName.Zibal, title: 'زیبال' },
    { serviceName: GatewayName.Vandar, title: 'وندار' },
    { serviceName: GatewayName.Jibit, title: 'جیبیت' },
    { serviceName: GatewayName.PayStar, title: 'پی‌استار' },
];

/* ---------------- انتخاب درگاه فعال ---------------- */
const GatewaySelectorTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData, reload } = useLoadSettings<GatewaySettingDto>(API.siteSettings.gateway, setCardLoading);
    const { requestAuth, modal } = useGoogleAuth();

    // Merge known gateways with whatever the backend returned.
    const rows: GatewaySettingItem[] = GATEWAYS.map((g) => {
        const existing = data?.items?.find((i) => i.serviceName === g.serviceName);
        return {
            serviceName: g.serviceName,
            apiKey: existing?.apiKey ?? '',
            isDepositActive: existing?.isDepositActive ?? false,
            isWithdrawActive: existing?.isWithdrawActive ?? false,
        };
    });

    const update = (serviceName: GatewayName, changes: Partial<GatewaySettingItem>) => {
        const next = rows.map((r) => {
            if (r.serviceName === serviceName) return { ...r, ...changes };
            // enforce single deposit/withdraw active across gateways
            const off: Partial<GatewaySettingItem> = {};
            if (changes.isDepositActive) off.isDepositActive = false;
            if (changes.isWithdrawActive) off.isWithdrawActive = false;
            return { ...r, ...off };
        });
        setData({ items: next });
    };

    const saveGateway = (row: GatewaySettingItem) => {
        requestAuth(async (code) => {
            const res: any = await postViaAuth(API.siteSettings.changeGateway, {
                serviceName: row.serviceName,
                apiKey: row.apiKey,
                isDepositActive: row.isDepositActive,
                isWithdrawActive: row.isWithdrawActive,
                googleAuthCode: code,
            });
            if (responseValidator(res.status)) {
                message.success('درگاه با موفقیت ذخیره شد.');
                reload();
                return true;
            }
            return false;
        });
    };

    return (
        <div className="settings-section">
            <div className="settings-section__head">
                <h3>درگاه‌های پرداخت</h3>
                <p>کلید هر درگاه را تنظیم و وضعیت واریز/برداشت آن را مشخص کنید. در هر لحظه تنها یک درگاه می‌تواند برای واریز و یک درگاه برای برداشت فعال باشد. ذخیره هر درگاه نیازمند تایید دو مرحله‌ای است.</p>
            </div>
            <div className="gateway-cards">
                {rows.map((row) => (
                    <div
                        className={`gateway-card ${row.isDepositActive || row.isWithdrawActive ? 'is-active' : ''}`}
                        key={row.serviceName}
                    >
                        <div className="gateway-card__head">
                            <h4>{GATEWAYS.find((g) => g.serviceName === row.serviceName)?.title}</h4>
                        </div>
                        <TextInput
                            label="API Key"
                            value={row.apiKey}
                            onChange={(v) => update(row.serviceName, { apiKey: v })}
                        />
                        <div className="gateway-card__toggles">
                            <SwitchField
                                label="واریز فعال"
                                checked={row.isDepositActive}
                                onChange={(c) => update(row.serviceName, { isDepositActive: c })}
                            />
                            <SwitchField
                                label="برداشت فعال"
                                checked={row.isWithdrawActive}
                                onChange={(c) => update(row.serviceName, { isWithdrawActive: c })}
                            />
                        </div>
                        <Button
                            type={buttonType.info}
                            htmlType="button"
                            text="ذخیره و تایید"
                            onClick={() => saveGateway(row)}
                        />
                    </div>
                ))}
            </div>
            {modal}
        </div>
    );
};

/* ---------------- جیبیت ---------------- */
const JibitTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<JibitPaymentSettings>(
        API.siteSettings.jibitPaymentSettings,
        setCardLoading,
    );
    const { requestAuth, modal } = useGoogleAuth();

    const save = () => {
        requestAuth(async (code) => {
            const res: any = await postViaAuth(API.siteSettings.changeJibitPaymentSettings, {
                secretKey: data?.secretKey ?? '',
                cashInApiKey: data?.cashInApiKey ?? '',
                cashInSecretKey: data?.cashInSecretKey ?? '',
                widhtrawApiKey: data?.widhtrawApiKey ?? '',
                withdrawsecretKey: data?.withdrawsecretKey ?? '',
                googleAuthCode: code,
            });
            if (responseValidator(res.status)) {
                message.success('تنظیمات جیبیت ذخیره شد.');
                return true;
            }
            return false;
        });
    };

    return (
        <>
            <SettingsSection
                title="درگاه جیبیت"
                description="کلیدهای واریز (Cash-In) و برداشت جیبیت. ذخیره نیازمند تایید دو مرحله‌ای است."
                onSave={save}
                saveText="ذخیره و تایید"
            >
                <TextInput label="Secret Key" htmlType="password" value={data?.secretKey} onChange={(v) => setData({ ...data, secretKey: v })} />
                <TextInput label="Cash-In API Key" value={data?.cashInApiKey} onChange={(v) => setData({ ...data, cashInApiKey: v })} />
                <TextInput label="Cash-In Secret Key" htmlType="password" value={data?.cashInSecretKey} onChange={(v) => setData({ ...data, cashInSecretKey: v })} />
                <TextInput label="Withdraw API Key" value={data?.widhtrawApiKey} onChange={(v) => setData({ ...data, widhtrawApiKey: v })} />
                <TextInput label="Withdraw Secret Key" htmlType="password" value={data?.withdrawsecretKey} onChange={(v) => setData({ ...data, withdrawsecretKey: v })} />
            </SettingsSection>
            {modal}
        </>
    );
};

/* ---------------- وندار ---------------- */
const VandarTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<VandarSettings>(API.siteSettings.vandarSettings, setCardLoading);
    const { requestAuth, modal } = useGoogleAuth();

    const save = () => {
        requestAuth(async (code) => {
            const res: any = await postViaAuth(API.siteSettings.changeVandarSettings, {
                token: data?.token ?? '',
                refreshToken: data?.refreshToken ?? '',
                businessName: data?.businessName ?? '',
                googleAuthCode: code,
            });
            if (responseValidator(res.status)) {
                message.success('تنظیمات وندار ذخیره شد.');
                return true;
            }
            return false;
        });
    };

    return (
        <>
            <SettingsSection
                title="درگاه وندار"
                description="توکن‌ها و نام کسب‌وکار وندار. ذخیره نیازمند تایید دو مرحله‌ای است."
                onSave={save}
                saveText="ذخیره و تایید"
            >
                <TextInput label="Token" htmlType="password" value={data?.token} onChange={(v) => setData({ ...data, token: v } as VandarSettings)} />
                <TextInput label="Refresh Token" htmlType="password" value={data?.refreshToken} onChange={(v) => setData({ ...data, refreshToken: v } as VandarSettings)} />
                <TextInput label="نام کسب‌وکار (Business Name)" value={data?.businessName} onChange={(v) => setData({ ...data, businessName: v } as VandarSettings)} />
            </SettingsSection>
            {modal}
        </>
    );
};

/* ---------------- زیبال ---------------- */
const ZibalTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<ZibalSettings>(API.siteSettings.zibalSettings, setCardLoading);
    const { requestAuth, modal } = useGoogleAuth();

    const save = () => {
        requestAuth(async (code) => {
            const res: any = await postViaAuth(API.siteSettings.changeZibalSettings, {
                apiKey: data?.apiKey ?? '',
                walletId: data?.walletId ?? '',
                googleAuthCode: code,
            });
            if (responseValidator(res.status)) {
                message.success('تنظیمات زیبال ذخیره شد.');
                return true;
            }
            return false;
        });
    };

    return (
        <>
            <SettingsSection
                title="درگاه زیبال"
                description="کلید و شناسه کیف‌پول زیبال. ذخیره نیازمند تایید دو مرحله‌ای است."
                onSave={save}
                saveText="ذخیره و تایید"
            >
                <TextInput label="API Key" value={data?.apiKey} onChange={(v) => setData({ ...data, apiKey: v } as ZibalSettings)} />
                <TextInput label="Wallet ID" value={data?.walletId} onChange={(v) => setData({ ...data, walletId: v } as ZibalSettings)} />
            </SettingsSection>
            {modal}
        </>
    );
};

/* ---------------- پی‌استار ---------------- */
const PayStarTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<PayStarSettings>(API.siteSettings.paystarSettings, setCardLoading);
    const { requestAuth, modal } = useGoogleAuth();

    const set = (changes: Partial<PayStarSettings>) => setData({ ...(data as PayStarSettings), ...changes });

    const save = () => {
        requestAuth(async (code) => {
            const res: any = await postViaAuth(API.siteSettings.changePaystarSettings, {
                key: data?.key ?? '',
                walletHashId: data?.walletHashId ?? '',
                password: data?.password ?? '',
                withdrawlSign: data?.withdrawlSign ?? '',
                withdrawlApiKey: data?.withdrawlApiKey ?? '',
                refreshTokenWallet: data?.refreshTokenWallet ?? '',
                applicationId: data?.applicationId ?? '',
                accessPassword: data?.accessPassword ?? '',
                refreshTokenCoBank: data?.refreshTokenCoBank ?? '',
                accountNumber: data?.accountNumber ?? '',
                googleAuthCode: code,
            });
            if (responseValidator(res.status)) {
                message.success('تنظیمات پی‌استار ذخیره شد.');
                return true;
            }
            return false;
        });
    };

    return (
        <>
            <SettingsSection
                title="درگاه پی‌استار"
                description="اطلاعات کیف‌پول و هم‌بانک پی‌استار. ذخیره نیازمند تایید دو مرحله‌ای است."
                onSave={save}
                saveText="ذخیره و تایید"
            >
                <TextInput label="Key" value={data?.key} onChange={(v) => set({ key: v })} />
                <TextInput label="Wallet Hash ID" value={data?.walletHashId} onChange={(v) => set({ walletHashId: v })} />
                <TextInput label="Password" htmlType="password" value={data?.password} onChange={(v) => set({ password: v })} />
                <TextInput label="Withdrawl Sign" value={data?.withdrawlSign} onChange={(v) => set({ withdrawlSign: v })} />
                <TextInput label="Withdrawl API Key" value={data?.withdrawlApiKey} onChange={(v) => set({ withdrawlApiKey: v })} />
                <TextInput label="Refresh Token (Wallet)" htmlType="password" value={data?.refreshTokenWallet} onChange={(v) => set({ refreshTokenWallet: v })} />
                <TextInput label="Application ID" value={data?.applicationId} onChange={(v) => set({ applicationId: v })} />
                <TextInput label="Access Password" htmlType="password" value={data?.accessPassword} onChange={(v) => set({ accessPassword: v })} />
                <TextInput label="Refresh Token (CoBank)" htmlType="password" value={data?.refreshTokenCoBank} onChange={(v) => set({ refreshTokenCoBank: v })} />
                <TextInput label="شماره حساب" value={data?.accountNumber} onChange={(v) => set({ accountNumber: v })} />
            </SettingsSection>
            {modal}
        </>
    );
};

/* ---------------- پاکت ---------------- */
const PakatTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<PakatSettings>(API.siteSettings.pakatSetting, setCardLoading);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.changePakatSetting, { apiKeyV3: data?.apiKeyV3 ?? '' });
        setSaving(false);
        if (responseValidator(res.status)) message.success('تنظیمات پاکت ذخیره شد.');
    };

    return (
        <SettingsSection title="درگاه پاکت" description="کلید نسخه ۳ سرویس پاکت." onSave={save} loading={saving}>
            <TextInput label="API Key V3" value={data?.apiKeyV3} onChange={(v) => setData({ ...data, apiKeyV3: v } as PakatSettings)} />
        </SettingsSection>
    );
};

export const PaymentGatewaysSettings: FC = () => {
    const [cardLoading, setCardLoading] = useState(false);

    const items = [
        { key: 'active', label: 'درگاه فعال', icon: <SafetyOutlined />, children: <GatewaySelectorTab setCardLoading={setCardLoading} /> },
        { key: 'jibit', label: 'جیبیت', icon: <ApiOutlined />, children: <JibitTab setCardLoading={setCardLoading} /> },
        { key: 'vandar', label: 'وندار', icon: <WalletOutlined />, children: <VandarTab setCardLoading={setCardLoading} /> },
        { key: 'zibal', label: 'زیبال', icon: <GoldOutlined />, children: <ZibalTab setCardLoading={setCardLoading} /> },
        { key: 'paystar', label: 'پی‌استار', icon: <CreditCardOutlined />, children: <PayStarTab setCardLoading={setCardLoading} /> },
        { key: 'pakat', label: 'پاکت', icon: <BankOutlined />, children: <PakatTab setCardLoading={setCardLoading} /> },
    ];

    return (
        <Card blur={cardLoading} className="pmx-settings-page">
            <Tabs items={items} destroyInactiveTabPane />
        </Card>
    );
};
